import React from "react";
import DataHistoryVM from "../viewmodel/DataHistoryVM";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileIcon from "@mui/icons-material/InsertDriveFile";
import HomeIcon from "@mui/icons-material/Home";
import HistoryIcon from "@mui/icons-material/History";
import CircularProgress from "@mui/material/CircularProgress";
import CBackDrop from "../../../basecomp/components/CBackDrop";
import CText from "../../../basecomp/newComponents/CText";
import CTable from "../../../basecomp/newComponents/CTable";
import CButton from "../../../basecomp/newComponents/CButton";
import CBox from "../../../basecomp/newComponents/CBox";
import CThemeProvider from "../../../basecomp/newComponents/CThemeProvider";
import { conasTheme } from "../../../basecomp/newComponents/CTheme";
import CCard from "../../../basecomp/newComponents/CCard";
import CTooltip from "../../../basecomp/newComponents/CTooltip";
import CChip from "../../../basecomp/newComponents/CChip";
import CInput from "../../../basecomp/newComponents/CInput";
import CModal from "../../../basecomp/newComponents/CModal";
import CRadioGroup from "../../../basecomp/newComponents/CRadioButton";
import EnrichmentDetailTable from "./DetailedPage";

const HistoryPage = () => {
  const {
    // State values
    pageTitle,
    searchTerm,
    page,
    rowsPerPage,
    filteredRecords,
    isLoading,
    isDownloading,
    showDetailView,
    selectedBatch,
    selectedCustomer,
    selectedProject,
    isEnrichmentHistory,
    downloadDialogOpen,
    dialogDownloadLoading,
    selectedStatus,
    statusOptions,
    columns,

    // New Data Matching modal states
    dataMatchingDownloadModalOpen,
    selectedFileType,
    fileTypeOptions,
    isDataMatchingTool,

    // Event handlers
    handleSearchChange,
    handleChangePage,
    handleChangeRowsPerPage,
    handleGoHome,
    handleCloseBackdrop,
    handleBackToHistory,
    handleDownloadDialogClose,
    handleConfirmDownload,
    setSelectedStatus,

    // New Data Matching modal handlers
    handleDataMatchingDownload,
    handleDataMatchingDownloadModalClose,
    setSelectedFileType,

    // Utility functions
    getStatusCellConfig
  } = DataHistoryVM();

  // Render cell based on column configuration
  const renderCell = (cellConfig, record) => {
    if (!cellConfig) return null;

    switch (cellConfig.type) {
      case "text":
        return cellConfig.content;
      case "chip":
        return (
          <CChip
            label={cellConfig.config.label}
            color={cellConfig.config.color}
            variant={cellConfig.config.variant}
          />
        );
      case "button":
        const statusConfig = getStatusCellConfig(record, isEnrichmentHistory);
        return (
          <CButton
            type="custom"
            onClick={statusConfig.config.onClick}
            disabled={statusConfig.config.disabled}
            size="small"
            label={statusConfig.config.label}
            sx={statusConfig.config.sx}
          />
        );
      case "iconButton":
        return (
          <CBox sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
            <CTooltip title={cellConfig.config.tooltip}>
              <span>
                <CButton
                  type="icon"
                  customIcon={cellConfig.config.icon}
                  onClick={cellConfig.config.onClick}
                  size="small"
                  disabled={cellConfig.config.disabled}
                  iconButton={true}
                />
              </span>
            </CTooltip>
          </CBox>
        );
      case "box":
        return (
          <CBox sx={cellConfig.config.sx}>
            {cellConfig.config.content}
          </CBox>
        );
      default:
        return cellConfig;
    }
  };

  // Transform columns for CTable component
  const tableColumns = columns.map(col => ({
    ...col,
    render: col.render ? (record) => {
      const cellConfig = col.render(record);
      return renderCell(cellConfig, record);
    } : undefined
  }));

  return (
    <CThemeProvider theme={conasTheme}>
      <CBackDrop 
        cOpen={isDownloading} 
        cHandleClose={handleCloseBackdrop} 
        cAllowClickToClose={false} 
      />
      <CBox sx={{ 
        width: "100%", 
        mt: "100px", 
        display: "flex", 
        flexDirection: "column", 
        alignItems: "center" 
      }}>
        {isEnrichmentHistory && showDetailView ? (
          <EnrichmentDetailTable
            batchId={selectedBatch}
            customerName={selectedCustomer}
            projectName={selectedProject}
            onBack={handleBackToHistory}
          />
        ) : (
          <CBox sx={{ 
            width: "100%", 
            maxWidth: "1920px", 
            px: { xs: 2, sm: 3, md: 4 }, 
            boxSizing: "border-box" 
          }}>
            {/* Header Section */}
            <CBox sx={{ 
              display: "flex", 
              justifyContent: "space-between", 
              alignItems: "center", 
              mb: { xs: 1, sm: 1.5 } 
            }}>
              <CText
                variant="h5"
                strong={true}
                icon={<HistoryIcon />}
                sx={{ 
                  color: conasTheme.palette.primary.main, 
                  marginLeft: "20px", 
                  fontSize: { xs: "1.2rem", sm: "1.5rem" } 
                }}
              >
                {pageTitle}
              </CText>
              <CButton
                type="custom"
                variant="outlined"
                color="primary"
                size="medium"
                customIcon={<HomeIcon />}
                iconPosition="start"
                onClick={handleGoHome}
                label="Home"
                sx={{ marginRight: "50px" }}
              />
            </CBox>

            {/* Main Content Card */}
            <CCard
              cards={[
                {
                  id: "processed-records",
                  header: "",
                  value: "",
                  content: (
                    <>
                      <CBox sx={{ 
                        display: "flex", 
                        flexDirection: { xs: "column", sm: "row" }, 
                        justifyContent: "space-between", 
                        alignItems: { xs: "stretch", sm: "center" }, 
                        mb: 3, 
                        gap: { xs: 2, sm: 0 } 
                      }}>
                        <CText
                          variant="h6"
                          icon={<FileIcon />}
                          sx={{ 
                            fontSize: { xs: "1rem", sm: "1.25rem" }, 
                            flexShrink: 0 
                          }}
                        >
                          {isEnrichmentHistory ? "Enrichment Records" : "Processed Records"}
                        </CText>
                        <CInput
                          placeholder="Search..."
                          value={searchTerm}
                          onChange={handleSearchChange}
                          size="small"
                          variant="outlined"
                          fullWidth={true}
                          sx={{ 
                            width: { xs: "100%", sm: "auto" }, 
                            minWidth: { xs: "100%", sm: 250 } 
                          }}
                        />
                      </CBox>
                      <CBox sx={{ overflowX: "auto", width: "100%" }}>
                        <CTable
                          columns={tableColumns}
                          data={filteredRecords}
                          loading={isLoading}
                          showSearch={false}
                          page={page}
                          rowsPerPage={rowsPerPage}
                          onPageChange={handleChangePage}
                          onRowsPerPageChange={handleChangeRowsPerPage}
                          defaultRowsPerPage={25}
                          rowsPerPageOptions={[5, 10, 25, 50, 100]}
                          emptyMessage="No records found"
                          tableProps={{
                            size: "small",
                            "aria-label": isEnrichmentHistory ? "enrichment records table" : "history records table",
                            sx: {
                              minWidth: "100%",
                              "& .MuiTableCell-root": {
                                whiteSpace: "nowrap",
                                padding: { xs: "8px 6px", sm: "16px 8px" }
                              },
                              "& .MuiTableCell-head": {
                                fontSize: { xs: "0.75rem", sm: "0.875rem" }
                              }
                            }
                          }}
                        />
                      </CBox>
                    </>
                  )
                }
              ]}
              sx={{ 
                p: { xs: 1.5, sm: 2, md: 3 }, 
                pt: { xs: 1, sm: 1.5, md: 2 }, 
                borderRadius: 2, 
                bgcolor: conasTheme.palette.background.paper, 
                width: "100%", 
                boxSizing: "border-box", 
                maxWidth: "100%", 
              }}
              fullWidth={true}
              elevation={3}
            />
          </CBox>
        )}
      </CBox>

      {/* Enrichment Download Dialog Modal */}
      <CModal
        open={downloadDialogOpen}
        handleClose={handleDownloadDialogClose}
        title="Download Materials"
        maxWidth="xs"
        fullWidth={true}
        actions={[
          {
            label: "Cancel",
            onClick: handleDownloadDialogClose,
            disabled: dialogDownloadLoading,
            variant: "outlined"
          },
          {
            label: dialogDownloadLoading ? "Downloading..." : "Download",
            onClick: handleConfirmDownload,
            disabled: dialogDownloadLoading || !selectedStatus,
            startIcon: dialogDownloadLoading ? <CircularProgress size={20} /> : <FileDownloadIcon />,
            color: "primary"
          }
        ]}
      >
        <CBox sx={{ p: 2 }}>
          <CText variant="body1" sx={{ mb: 2 }}>
            Please select the status of records you want to download:
          </CText>
          <CRadioGroup
            label=""
            options={statusOptions}
            value={selectedStatus}
            onChange={(value) => setSelectedStatus(value)}
            disabled={dialogDownloadLoading}
            name="download-status"
            sx={{ mt: 1 }}
          />
        </CBox>
      </CModal>

      {/* New Data Matching Download Dialog Modal */}
      <CModal
        open={dataMatchingDownloadModalOpen}
        handleClose={handleDataMatchingDownloadModalClose}
        title="Download Data Matching Files"
        maxWidth="xs"
        fullWidth={true}
        actions={[
          {
            label: "Cancel",
            onClick: handleDataMatchingDownloadModalClose,
            disabled: isDownloading,
            variant: "outlined"
          },
          {
            label: isDownloading ? "Downloading..." : "Download",
            onClick: handleDataMatchingDownload,
            disabled: isDownloading || !selectedFileType,
            startIcon: isDownloading ? <CircularProgress size={20} /> : <FileDownloadIcon />,
            color: "primary"
          }
        ]}
      >
        <CBox sx={{ p: 2 }}>
          <CText variant="body1" sx={{ mb: 2 }}>
            Please select which file(s) you want to download:
          </CText>
          <CRadioGroup
            label=""
            options={fileTypeOptions}
            value={selectedFileType}
            onChange={(value) => setSelectedFileType(value)}
            disabled={isDownloading}
            name="file-type-selection"
            sx={{ mt: 1 }}
          />
        </CBox>
      </CModal>
    </CThemeProvider>
  );
};

export default HistoryPage;
